<template>
    <div class="container-fluid">
        <div class="row" v-if="$slots.header">
            <slot name="header"/>
        </div>
        <h4 v-if="searchEnabled && title" class="m-0 pb-2">{{ title }}</h4>
        <div class="row">
            <div v-if="searchEnabled" class="col-lg-4 col-xs-12 el-header-table">
                <div class="pull-left col-12 p-0">
                    <fg-input round class="input-sm"
                              style="width: 100%;"
                              :placeholder="$t('Search...')"
                              :value="searchQuery"
                              @input="val => $emit('search-change', val)"
                              addon-right-icon="nc-icon nc-zoom-split">
                    </fg-input>
                </div>
            </div>
            <div v-else class="col-lg-4 col-md-4 col-xs-12 el-header-table">
                <h4 v-if="title" class="d-inline m-0">{{ title }}</h4>
            </div>
            <div v-if="actions" class="col-lg-8 col-md-12 col-xs-12 p-0 text-right">
                <el-badge v-if="invite && invite.openInvites > 0" :value="invite.openInvites" :max="99" class="item ml-3 mr-3">
                    <p-button class="mt-0" round type="default" @click="invite.click">{{$t('Invite User')}}</p-button>
                </el-badge>
                <div class="col-xs-12 text-right">
                  <p-button class="mt-0 ml-3 mr-3 btn-one-size responsive-create-button" v-for="(action,i) in actions" round type="default" @click="action.click" :key="`action_${i}`">{{$t(action.label)}}</p-button>
                </div>
            </div>
        </div>
        <div class="card-body row pt-0">
            <p v-if="pagination && pagination.total > pagination.perPage" class="category text-end pagination-label mb-0">
                {{$tc('Showing {from} - {to} of {total} Entries', pagination.total, { from, to, total: pagination.total })}}
            </p>
            <div v-if="isDragable" class="col-12 p-0 table-responsive table-condensed">
                <el-table-draggable :animate="500">
                    <el-table class="table-striped"
                              v-loading="loading"
                              :element-loading-text="$t('Loading...')"
                              :show-header="!hideHeader"
                              header-row-class-name="text-primary"
                              @sort-change="sortChange"
                              :default-sort="defaultSort"
                              :data="value">
                        <el-table-column key="column.name"
                                         width="40"
                                         min-width="10px"
                                         label=""
                                         align="center">
                            <div style="font-size: 20px;" class="grab">
                                <i class="fa fa-grip-vertical fa-sm"/>
                            </div>
                        </el-table-column>
                        <el-table-column v-if="window.width >= 768"
                                         v-for="column in columns"
                                         :key="column.name"
                                         :sortable="column.sortable"
                                         :width="column.width || 'auto'"
                                         :min-width="column.minWidth || '100px'"
                                         :prop="getProp(column.name)"
                                         :formatter="column.formatter"
                                         :label="$t(column.text)">
                        </el-table-column>
                        <el-table-column
                                v-if="(edit || remove || enter || $scopedSlots.actionsByRow) && window.width >= 768"
                                :min-width="actionsMinWidth || 120"
                                :width="actionsMinWidth || 120"
                                align="center"
                                fixed="right"
                                class-name="td-actions"
                                :label="$tc('action', countActions())">
                            <template slot-scope="props">
                                <p-button v-if="remove" round type="danger" size="sm" icon @click="remove(props)">
                                    <i class="fa fa-times"/>
                                </p-button>
                                <p-button v-if="edit" round type="success" size="sm" icon @click="edit(props)">
                                    <i class="fa fa-edit"/>
                                </p-button>
                                <p-button v-if="enter" round type="success" size="sm" icon @click="enter(props)">
                                    <i class="fa fa-arrow-right"/>
                                </p-button>
                                <slot name="actionsByRow" :row="props.row"/>
                            </template>
                        </el-table-column>
                        <el-table-column v-if="window.width < 768" key="smallWindow"
                                         :sortable="false"
                                         :width="'auto'"
                                         :min-width="'100px'">
                            <template slot-scope="props">
                                <div v-for="column in columns" class="row" style="margin-top: 3px">
                                    <div class="col-4 custom-title">{{$t(column.text)}}</div>
                                    <div v-if="column.formatter" class="col-8">{{column.formatter(props.row, column.text, props.row[column.name])}}</div>
                                    <div v-else class="col-8">{{props.row[column.name]}}</div>
                                </div>
                                <div v-if="$scopedSlots.chartByRow" class="row">
                                    <div class="col-4 title"></div>
                                    <div class="col-8">
                                        <slot name="chartByRow" :row="props.row"/>
                                    </div>
                                </div>
                                <div v-if="$scopedSlots.average && hideAverage" class="row">
                                    <div class="col-4 title">{{$t('Average')}}</div>
                                    <div class="col-8">
                                        <slot name="average" :row="props.row"/>
                                    </div>
                                </div>
                                <div v-if="(edit || remove || enter || $scopedSlots.actionsByRow)" class="row" style="margin-top: 7px">
                                    <div class="col-4 title">{{$t('Actions')}}</div>
                                    <div class="col-8">
                                        <p-button v-if="remove" round type="danger" size="sm" icon @click="remove(props)">
                                            <i class="fa fa-times"/>
                                        </p-button>
                                        <p-button v-if="edit" round type="success" size="sm" icon @click="edit(props)">
                                            <i class="fa fa-edit"/>
                                        </p-button>
                                        <p-button v-if="enter" round type="primary" size="sm" icon @click="enter(props)">
                                            <i class="fa fa-arrow-right"/>
                                        </p-button>
                                        <slot name="actionsByRow" :row="props.row"/>
                                    </div>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-table-draggable>
            </div>
            <div v-else class="col-12 p-0 table-responsive">
                <el-table class="table-striped"
                          border
                          v-loading="loading"
                          :element-loading-text="$t('Loading...')"
                          :show-header="!hideHeader"
                          header-row-class-name="text-primary"
                          @sort-change="sortChange"
                          :default-sort="defaultSort"
                          :row-class-name="tableRowClassName"
                          :data="value">
                    <el-table-column v-if="window.width >= 768"
                                     v-for="column in columns"
                                     :key="column.name"
                                     :sortable="column.sortable"
                                     :width="column.width || 'auto'"
                                     :min-width="column.minWidth || '100px'"
                                     :prop="getProp(column.name)"
                                     :formatter="column.formatter"
                                     :label="$t(column.text)">
                    </el-table-column>
                    <el-table-column
                            v-if="$scopedSlots.chartByRow && window.width >= 768"
                            width="auto"
                            align="center">
                        <template slot-scope="props">
                            <slot name="chartByRow" :row="props.row"/>
                        </template>
                    </el-table-column>
                    <el-table-column
                            v-if="$scopedSlots.average && window.width >= 768 && hideAverage"
                            width="auto"
                            align="center"
                            :label="$t('Average')">
                        <template slot-scope="props">
                            <slot name="average" :row="props.row"/>
                        </template>
                    </el-table-column>
                    <el-table-column
                            v-if="(edit || remove || enter || $scopedSlots.actionsByRow) && window.width >= 768"
                            :min-width="actionsMinWidth || 120"
                            :width="actionsMinWidth || 120"
                            align="center"
                            class-name="td-actions"
                            :label="actionColumnLabel ? actionColumnLabel : $tc('action', countActions())">
                        <template slot-scope="props">
                            <p-button v-if="remove" round type="danger" size="sm" icon @click="remove(props)">
                                <i class="fa fa-times"/>
                            </p-button>
                            <p-button v-if="edit" round type="success" size="sm" icon @click="edit(props)">
                                <i class="fa fa-edit"/>
                            </p-button>
                            <p-button v-if="enter" round type="primary" size="sm" icon @click="enter(props)">
                                <i class="fa fa-arrow-right"/>
                            </p-button>
                            <slot name="actionsByRow" :row="props.row"/>
                        </template>
                    </el-table-column>
                    <el-table-column v-if="window.width < 768" key="smallWindow"
                                     :sortable="false"
                                     :width="'auto'"
                                     :min-width="'100px'">
                        <template slot-scope="props">
                            <div v-if="removeTitleOnMobile" v-for="column in columns" class="row" style="margin-top: 3px">
                              <div v-if="column.formatter" class="col-12">{{column.formatter(props.row, column.text, props.row[column.name])}}</div>
                              <div v-else-if="column.name === 'user.username'" class="col-12">{{props.row.user.username}}</div>
                              <div v-else class="col-12">{{props.row[column.name]}}</div>
                            </div>
                            <div v-if="!removeTitleOnMobile" v-for="column in columns" class="row" style="margin-top: 3px">
                              <div class="col-4 custom-title">{{$t(column.text)}}</div>
                              <div v-if="column.formatter" class="col-8">{{column.formatter(props.row, column.text, props.row[column.name])}}</div>
                              <div v-else-if="column.name === 'user.username'" class="col-8">{{props.row.user.username}}</div>
                              <div v-else class="col-8">{{props.row[column.name]}}</div>
                            </div>
                            <div v-if="$scopedSlots.chartByRow" class="row">
                                <div class="col-4 title"></div>
                                <div class="col-8">
                                    <slot name="chartByRow" :row="props.row"/>
                                </div>
                            </div>
                            <div v-if="$scopedSlots.average && hideAverage" class="row">
                                <div class="col-4 title">{{$t('Average')}}</div>
                                <div class="col-8">
                                    <slot name="average" :row="props.row"/>
                                </div>
                            </div>
                            <div v-if="(edit || remove || enter || $scopedSlots.actionsByRow)" class="row" style="margin-top: 7px">
                                <div v-if="!removeTitleOnMobile" class="col-4 custom-title">{{$t('Actions')}}</div>
                                <div class="col-8">
                                    <p-button v-if="remove" round type="danger" size="sm" icon @click="remove(props)">
                                        <i class="fa fa-times"/>
                                    </p-button><span v-if="remove">&nbsp;</span>
                                    <p-button v-if="edit" round type="success" size="sm" icon @click="edit(props)">
                                        <i class="fa fa-edit"/>
                                    </p-button><span v-if="edit">&nbsp;</span>
                                    <p-button v-if="enter" round type="primary" size="sm" icon @click="enter(props)">
                                        <i class="fa fa-arrow-right"/>
                                    </p-button><span v-if="enter">&nbsp;</span>
                                    <slot name="actionsByRow" :row="props.row"/>
                                </div>
                            </div>
                        </template>
                    </el-table-column>
                </el-table>
            </div>
            <div v-if="pagination" class="col-12">
                <div class="col-sm-12 pt-3 m-0">
                    <p-pagination class="pull-center"
                                  :value="pagination.currentPage"
                                  @input="changePage"
                                  :per-page="pagination.perPage"
                                  :total="pagination.total">
                    </p-pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
  import Vue from 'vue'
  import { Tooltip, Table, TableColumn, Badge } from 'element-ui'
  import PPagination from 'src/components/UIComponents/Pagination.vue'
  import ElTableDraggable from './SortableElTable'
  import has from 'lodash/has'
  import get from 'lodash/get'

  import elementLangDe from 'element-ui/lib/locale/lang/de';
  import elementLangEn from 'element-ui/lib/locale/lang/en';
  import elementLocale from 'element-ui/lib/locale';

  Vue.use(Table)
  Vue.use(TableColumn)
  Vue.use(TableColumn)
  Vue.use(Badge)

  export default {
    name: 'BasicList',

    components: {
      PPagination,
      ElTableDraggable,
      [Tooltip.name]: Tooltip
    },

    props: {
      value: {
        type: Array
      },
      pagination: {
        type: Object
      },
      columns: {
        type: Array
      },
      title: {
        type: String
      },
      actions: {
        type: Array,
        default: null
      },
      edit: {
        type: Function,
        default: null
      },
      loading: {
        type: Boolean,
        default: () => false
      },
      remove: {
        type: Function,
        default: null
      },
      enter: {
        type: Function,
        default: null
      },
      hideHeader: {
        type: Boolean,
        default: () => false
      },
      hideAverage: {
        type: Boolean,
        default: () => true
      },
      defaultSort: {
        type: Object
      },
      sortChange: {
        type: Function,
        default: () => {}
      },
      searchQuery: {
        type: String,
        default: ''
      },
      searchEnabled: {
        type: Boolean,
        default: false
      },
      actionsMinWidth: {
        type: Number,
        default: null
      },
      actionsByRow: {
        type: Boolean,
        default: false
      },
      actionColumnLabel: {
        type: String,
        default: null
      },
      isDragable: {
        type: Boolean,
        default: false
      },
      tableRowClassName: {
        type: Function,
        default: () => ''
      },
      invite: {
        type: Object,
        default: null
      },
      onChangePage: {
        type: Function,
        default: null
      },
      removeTitleOnMobile: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        window: {
          width: 0
        }
      }
    },
    created() {
      window.addEventListener('resize', this.handleResize)
      this.handleResize();
    },
    methods: {
      changePage (val) {
        if (this.onChangePage) {
          this.onChangePage(val)
        } else {
          const page = val > 1 ? val : undefined
          this.$router.push({ ...this.$router.currentRoute, query: { ...this.$route.query, page } })
        }
      },
      getProp (name) {
        if (this.$i18n.locale === 'en') {
            elementLocale.use(elementLangEn);
        } else {
            elementLocale.use(elementLangDe);
        }
        const data = this.value && this.value[0] && get(this.value[0], name)
        if (has(data, this.$i18n.locale)) {
          return `${name}.${this.$i18n.locale}`
        } else {
          return name
        }
      },
      countActions () {
        let count = 0
        if (this.edit) count++
        if (this.remove) count++
        if (this.enter) count++
        if (this.$scopedSlots.actionsByRow) count++
        return count
      },
      handleResize() {
        this.window.width = window.innerWidth
      }
    },

    computed: {
      to () {
        let to = this.from + this.pagination.perPage - 1
        if (this.pagination.total < to) {
          to = this.pagination.total
        }
        return to
      },
      from () {
        if (this.pagination.total === 0) {
          return this.pagination.total
        } else {
          return this.pagination.perPage * (this.pagination.currentPage - 1) + 1
        }
      }
    },
  }
</script>
<style lang="scss" scoped>
    .el-table {
        &__fixed-right {
            background-color: #FFFFFF;
        }
    }
    .el-header-table {
        display: flex;
        align-items: flex-end;
    }
    .grab {cursor: grab;}

    .pagination-label {
        text-transform: initial;
    }

    .item {
        margin-top: 0;
    }

    .nowrap {
      white-space: normal;
      word-break: keep-all;
    }

    .modal-body {
      padding: 16px;
    }

    .custom-title {
      font-weight: bold;
      word-break: keep-all;
      white-space: normal;
      line-break: normal;
      word-wrap: normal;
      overflow: hidden;
    }

    @media (max-width: 768px) {
      .title-spacing {
        padding-left: 8px
      }
     }

    @media (max-width: 590px) {
      .responsive-create-button {
        width: 95%;
        margin-left: 5px; 
        margin-right: 5px;
      }

      .btn-one-size {
        width: 95%;
        margin-left: 5px; 
        margin-right: 5px;
      }

      .responsive-center {
          text-align: left !important;
      }
    }
</style>
